import { TransformHistory } from "./TransformHistory";
import { NetworkObject, NetworkObjectType } from "./NetworkObject";
import { BOOLEAN_BYTES_PER_ELEMENT, calcStringByteLength, ControllerHand, FacetVisibility, InputDeviceType, RaycastLine, RenderVisibilityMessage, UID_BYTES_PER_ELEMENT, UpdateFacetOnModelMessage, worldToLocalTransform } from "flavr-common";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { RequestRoomTransformMessage, RoomTransformMessage } from "../../../messages/scenegraph/ScenegraphMessages";
import { ControllerView, getControllerMeshInfo } from "../../inputs/controller";
import { SELECTION_STRATEGY } from "../../../messages/selection";
import { getOrCreateModelFromAbsolutePath, modelPool, modelToAbsolutePath, NO_MESSAGING } from "@modules/model";
import { ensurePlayerHeadModel, ensurePlayerRootModel, getPlayerControllerModelName, getPlayerMouseModelName } from "@modules/player";
const tmpControllerWorldTransform = new Matrix4();
const tmpControllerLocalTransform = new Matrix4();
const tmpControllerLocalScale = new Vector3();
/**
 *  Represents the Controller of a remote client.
 */
export class ControllerPeer extends NetworkObject {
    get constructorByteLength() {
        const controllerPath = this.getControllerPath();
        const roomPath = this.getRoomPath();
        return (
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        UID_BYTES_PER_ELEMENT + // controllerUid
            Uint32Array.BYTES_PER_ELEMENT + // baseColor
            Uint8Array.BYTES_PER_ELEMENT + // type
            Uint8Array.BYTES_PER_ELEMENT + // hand
            Float32Array.BYTES_PER_ELEMENT * 3 + // selectionRayOrigin
            Float32Array.BYTES_PER_ELEMENT * 3 + // selectionRayDirection
            BOOLEAN_BYTES_PER_ELEMENT + // hasTrackedHand
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            calcStringByteLength(controllerPath) +
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            calcStringByteLength(roomPath));
    }
    getRemoteControllerModelName() {
        const clientIdx = this.mgr.getClientIdxOrThrow(this.ownerId);
        if (this.type === InputDeviceType.Mouse) {
            return getPlayerMouseModelName(clientIdx);
        }
        return getPlayerControllerModelName(clientIdx, this.hand);
    }
    constructor(mgr, ownerId, netUid) {
        super(ownerId, netUid);
        this.mgr = mgr;
        // per-frame transform (pos + rot + scale) + hasTrackedHand
        this.byteLength = Float32Array.BYTES_PER_ELEMENT * (3 + 4 + 3) + BOOLEAN_BYTES_PER_ELEMENT;
        this.type = InputDeviceType.HtcViveController;
        this.hand = ControllerHand.Unknown;
        this.baseColor = 0x000000;
        this.hasTrackedHand = false;
        this.transformHistory = new TransformHistory();
        this.position = new Vector3();
        this.orientation = new Quaternion();
        this.scale = new Vector3(1, 1, 1);
        this.roomMatrix = new Matrix4();
        this.selectionVisible = true;
        this.selectionRayOrigin = [0, 0, 0];
        this.selectionRayDirection = [0, 0, 0];
        this.isVisible = true;
        this.pendingMessages = mgr.pendingBusMessages;
    }
    changeSelectionVisibility(visible) {
        if (visible === this.selectionVisible)
            return;
        this.selectionVisible = visible;
        if (this.view !== undefined) {
            this.view.showSelection(visible, this.pendingMessages);
        }
    }
    setLocalVisible(visible) {
        if (this.isVisible === visible)
            return;
        this.isVisible = visible;
        if (this.controllerUid === undefined)
            return;
        this.pendingMessages.push(new RenderVisibilityMessage(this.controllerUid, visible));
    }
    onRemoteCreate() {
        this.mgr.peersControllers.set(this.netUid, this);
        this.mgr.peerControllerCreatedDelegate.invoke(this);
        this.changeSelectionVisibility(this.mgr.peerSelectionVisible);
    }
    onRemoteRemove() {
        this.mgr.peersControllers.delete(this.netUid);
        if (this.currentRoomUID !== undefined) {
            this.mgr.peerControllerRemovedDelegate.invoke(this);
        }
        if (this.view) {
            this.view.deleteModel(this.pendingMessages);
            this.view = undefined;
        }
    }
    destroyFromClient() {
        this.destroy();
    }
    destroy() { }
    onRemoteRoomChanged(roomUID) {
        const isVisible = this.mgr.isModelVisibleForPeer(roomUID);
        this.currentRoomUID = roomUID;
        this.setLocalVisible(isVisible);
        if (isVisible) {
            this.mgr.connection
                .askByMessageType(new RequestRoomTransformMessage(roomUID), RoomTransformMessage)
                .then((msg) => {
                if (msg.worldMatrix !== undefined) {
                    this.roomMatrix.fromArray(msg.worldMatrix);
                }
                else {
                    this.setLocalVisible(false);
                }
            })
                .catch(() => {
                this.setLocalVisible(false);
            });
        }
        if (this.controllerUid === undefined)
            return;
        const visible = roomUID === this.mgr.currentRoomUID;
        this.pendingMessages.push(new UpdateFacetOnModelMessage(this.controllerUid, new FacetVisibility(RaycastLine, visible)));
    }
    onClientRoomChanged(roomUID) {
        if (this.controllerUid === undefined)
            return;
        if (this.currentRoomUID === undefined)
            return;
        const isVisible = this.mgr.isModelVisibleForPeer(this.currentRoomUID);
        this.setLocalVisible(isVisible);
        if (isVisible) {
            this.mgr.connection
                .askByMessageType(new RequestRoomTransformMessage(this.currentRoomUID), RoomTransformMessage)
                .then((msg) => {
                if (msg.worldMatrix !== undefined) {
                    this.roomMatrix.fromArray(msg.worldMatrix);
                }
                else {
                    this.setLocalVisible(false);
                }
            })
                .catch(() => {
                this.setLocalVisible(false);
            });
        }
        const visible = roomUID === this.currentRoomUID;
        this.pendingMessages.push(new UpdateFacetOnModelMessage(this.controllerUid, new FacetVisibility(RaycastLine, visible)));
    }
    onRoomTransformUpdate(matrix) {
        this.currentRoomUID && this.setLocalVisible(this.mgr.isModelVisibleForPeer(this.currentRoomUID));
        this.roomMatrix.fromArray(matrix);
    }
    onControllerMoved(position, orientation, scale) {
        this.position.fromArray(position);
        this.orientation.fromArray(orientation);
        this.scale.setScalar(scale);
    }
    constructorSerialization(stream) {
        var _a;
        let controllerUid = 0;
        if (!stream.isReading) {
            if (this.controllerUid === undefined)
                throw new Error("controllerUid not defined");
            controllerUid = this.controllerUid;
        }
        this.controllerUid = stream.uid(controllerUid);
        this.baseColor = stream.uint32(this.baseColor);
        this.type = stream.uint8(this.type);
        this.hand = stream.uint8(this.hand);
        this.selectionRayOrigin = stream.number3(this.selectionRayOrigin);
        this.selectionRayDirection = stream.number3(this.selectionRayDirection);
        this.hasTrackedHand = stream.boolean(this.hasTrackedHand);
        const roomPath = stream.utf16String((_a = this.getRoomPath()) !== null && _a !== void 0 ? _a : "");
        if (stream.isReading) {
            this.initRemote(roomPath);
        }
    }
    initRemote(roomPath) {
        var _a, _b;
        if (this.controllerUid === undefined)
            throw new Error("controllerUid not defined");
        const model = getOrCreateModelFromAbsolutePath(roomPath);
        this.currentRoomUID = model.getUID();
        const meshInfo = getControllerMeshInfo(this.type, this.hand);
        let selectionRayInfo;
        if (this.type !== InputDeviceType.Mouse && meshInfo.selection.strategy === SELECTION_STRATEGY.RAY_SELECT && meshInfo.selection.params.ray_visible) {
            selectionRayInfo = {
                origin: this.selectionRayOrigin,
                direction: this.selectionRayDirection,
                maxLength: (_a = meshInfo.selection.params.max_ray_length) !== null && _a !== void 0 ? _a : 100,
                color: (_b = meshInfo.selection.params.ray_color) !== null && _b !== void 0 ? _b : 0x0000ff
            };
        }
        const avatarRootUid = ensurePlayerRootModel(this.mgr.getClientIdxOrThrow(this.ownerId)).getUID();
        // For peer views: never pass hasTrackedHand=true – the static hand mesh (Hand_A_17) must
        // stay visible for remote peers because they have no local XR hand-joint rendering.
        // Instead, hide the controller-shape mesh when the peer is using hand-tracking.
        const showVrController = this.type !== InputDeviceType.Mouse && !this.hasTrackedHand && this.mgr.isShowingVrController;
        this.view = new ControllerView(this.controllerUid, showVrController, meshInfo, this.hand, this.baseColor, selectionRayInfo, avatarRootUid, this.getRemoteControllerModelName(), this.type, false // hasTrackedHand always false for peer view – hand mesh must remain visible
        );
        this.view.setShowAnnotations(false, this.pendingMessages);
        this.view.createModel(this.pendingMessages);
        if (this.type === InputDeviceType.Mouse) {
            const mouseModel = modelPool.getModel(this.controllerUid);
            const headModel = ensurePlayerHeadModel(this.mgr.getClientIdxOrThrow(this.ownerId));
            if (mouseModel !== undefined) {
                mouseModel.setParent(headModel, NO_MESSAGING);
                mouseModel.setTransformComponents(ControllerPeer.REMOTE_MOUSE_LOCAL_POSITION, ControllerPeer.REMOTE_MOUSE_LOCAL_ORIENTATION, ControllerPeer.REMOTE_MOUSE_LOCAL_SCALE, NO_MESSAGING);
            }
        }
        this.onRemoteRoomChanged(this.currentRoomUID);
    }
    serialization(stream) {
        const state = this.transformHistory.getFreeState();
        const roomWorldTransform = modelPool.getModelOrThrow(this.mgr.currentRoomUID).getWorldTransform();
        tmpControllerWorldTransform.compose(this.position, this.orientation, this.scale);
        worldToLocalTransform(tmpControllerWorldTransform, roomWorldTransform, tmpControllerLocalTransform);
        tmpControllerLocalTransform.decompose(state.position, state.rotation, tmpControllerLocalScale);
        state.position.x = stream.float32(state.position.x);
        state.position.y = stream.float32(state.position.y);
        state.position.z = stream.float32(state.position.z);
        state.rotation.x = stream.float32(state.rotation.x);
        state.rotation.y = stream.float32(state.rotation.y);
        state.rotation.z = stream.float32(state.rotation.z);
        state.rotation.w = stream.float32(state.rotation.w);
        state.scale.x = stream.float32(tmpControllerLocalScale.x);
        state.scale.y = stream.float32(tmpControllerLocalScale.y);
        state.scale.z = stream.float32(tmpControllerLocalScale.z);
        const prevHasTrackedHand = this.hasTrackedHand;
        this.hasTrackedHand = stream.boolean(this.hasTrackedHand);
        if (stream.isReading) {
            this.transformHistory.addState(state);
            // Propagate hasTrackedHand change to the peer's ControllerView.
            if (prevHasTrackedHand !== this.hasTrackedHand && this.view !== undefined) {
                // For peer views: only toggle the controller-mesh visibility.
                // Do NOT call setHasTrackedHand(true) – that would suppress the static hand mesh,
                // but remote peers have no local XR hand-joint rendering to replace it with.
                const showVrController = !this.hasTrackedHand && this.mgr.isShowingVrController;
                this.view.showVrController(showVrController, this.pendingMessages);
            }
        }
    }
    interpolate(time) {
        if (this.controllerUid === undefined)
            return;
        this.transformHistory.interpolate(time, 80, this.position, this.scale, this.orientation);
        if (this.view) {
            if (this.type === InputDeviceType.Mouse) {
                return;
            }
            this.view.update(this.pendingMessages, this.position, this.orientation, this.scale, this.roomMatrix, Object.assign(Object.assign({}, NO_MESSAGING), { updateFacet: false }));
        }
    }
    getControllerPath() {
        const controllerModel = this.controllerUid !== undefined ? modelPool.getModel(this.controllerUid) : undefined;
        return controllerModel !== undefined
            ? modelToAbsolutePath(controllerModel)
            : undefined;
    }
    getRoomPath() {
        const roomModel = this.mgr.currentRoomUID !== undefined ? modelPool.getModel(this.mgr.currentRoomUID) : undefined;
        return roomModel !== undefined
            ? modelToAbsolutePath(roomModel)
            : undefined;
    }
}
ControllerPeer.staticObjectType = NetworkObjectType.Controller;
// Keep the peer mouse visual fixed relative to the head. The actual mouse interaction ray
// is still driven by ControllerMovedMessage and screen-space mouse movement.
ControllerPeer.REMOTE_MOUSE_LOCAL_POSITION = [0.33, 0, -1];
ControllerPeer.REMOTE_MOUSE_LOCAL_ORIENTATION = [0, 0, 0, 1];
ControllerPeer.REMOTE_MOUSE_LOCAL_SCALE = [1, 1, 1];
